import { formatCifra } from './cifras';
import { currencyInfo } from './currency';
import type { Ingredient, MenuItem, Recipe, Tenant } from './types';
import { dimensionOf, isPurchaseUnit, toBase, type PurchaseUnit, type Unit } from './units';
import type { CatalogCompleteness } from './catalogCompleteness';
import { buildContext, computeRecipeCostPartial } from './costing';
import { planRecipeProposal } from './recipeProposal';
import type { Draft } from './drafts';

export type Question =
  | {
      id: string; kind: 'confirm-price'; ingredientId: string; text: string; unit: Unit; unitPrice?: number; options: string[];
      /**
       * Formato en el que se compra (rediseño, 11 oct 2026): nadie se sabe el
       * precio por kilo, pero si el de la bolsa. Si el ingrediente ya tiene
       * formato de compra, la pregunta va por ese formato y `packPrice` es el
       * precio estimado de ese formato.
       */
      presentation?: string; purchaseQty?: number; purchaseUnit?: PurchaseUnit; packPrice?: number;
      /** En cuantos platos se usa: «Está en 6 de tus recetas». */
      usedIn?: number;
    }
  | { id: string; kind: 'add-recipe'; menuItemId?: string; recipeId?: string; name?: string; text: string }
  | { id: string; kind: 'choose'; draftId: string; line: number; text: string; options: Array<{ ingredientId: string; name: string }> };
export interface QuestionHistory { skips: number; archivedUntil?: number; lastSkippedAt?: number }
export interface QuestionCatalog { ingredients: Ingredient[]; recipes: Recipe[]; menuItems: MenuItem[]; drafts?: Draft[]; history?: Record<string, QuestionHistory> }

export function nextQuestions(tenant: Pick<Tenant, 'currency' | 'locale'>, catalog: QuestionCatalog, completeness: CatalogCompleteness, limit = 1, now = Date.now()): Question[] {
  const context = buildContext(catalog.ingredients, catalog.recipes);
  const frequency = new Map<string, number>();
  const impact = new Map<string, number>();
  const sold = new Map<string, number>();
  for (const item of catalog.menuItems.filter((item) => item.active)) sold.set(item.recipeId, (sold.get(item.recipeId) ?? 0) + (item.unitsSold ?? 0));
  const hasSales = [...sold.values()].some((value) => value > 0);
  for (const recipe of catalog.recipes.filter((item) => item.active && item.kind === 'plato')) {
    const used = new Set<string>();
    const visit = (id: string, visited = new Set<string>()) => {
      if (visited.has(id)) return;
      visited.add(id);
      for (const line of context.recipes.get(id)?.components ?? []) {
        if (line.ref.kind === 'ingredient') used.add(line.ref.ingredientId);
        else visit(line.ref.recipeId, visited);
      }
    };
    visit(recipe.recipeId);
    used.forEach((id) => frequency.set(id, (frequency.get(id) ?? 0) + 1));
    try {
      const cost = computeRecipeCostPartial(recipe.recipeId, context);
      for (const line of cost.lines) {
        const ids = line.ref.kind === 'ingredient' ? [line.ref.ingredientId] : line.estimatedIngredientIds ?? [];
        ids.forEach((id) => impact.set(id, (impact.get(id) ?? 0) + line.cost / Math.max(1, cost.portions) * (hasSales ? sold.get(recipe.recipeId) ?? 0 : 1) / Math.max(1, ids.length)));
      }
    } catch { /* La falta de equivalencias no debe impedir preguntar por un precio. */ }
  }
  const language = (tenant.locale ?? 'es').split('-')[0];
  // Con el símbolo y las cifras de la moneda de la cuenta: «$290.00», nunca «290,00 MXN» (12 oct 2026).
  const money = new Intl.NumberFormat(currencyInfo(tenant.currency).locale, { style: 'currency', currency: currencyInfo(tenant.currency).code, currencyDisplay: 'narrowSymbol', useGrouping: true });
  const ranked: Array<{ question: Question; tier: number; score: number }> = [];
  for (const ingredient of catalog.ingredients.filter((item) => item.active && item.pricePending)) {
    const estimate = ingredient.priceEstimated;
    const unit = estimate?.unit ?? ingredient.purchaseUnit ?? 'kg';
    const pack = purchaseFormat(ingredient, unit);
    const packPrice = estimate && pack ? estimate.unitPrice * toBase(pack.qty, pack.unit) / toBase(1, unit) : undefined;
    const usedIn = frequency.get(ingredient.ingredientId) ?? 0;
    ranked.push({ tier: estimate ? 0 : 3, score: hasSales ? impact.get(ingredient.ingredientId) ?? 0 : usedIn + (impact.get(ingredient.ingredientId) ?? 0) / 1000000,
      question: { id: `price-${ingredient.ingredientId}`, kind: 'confirm-price', ingredientId: ingredient.ingredientId, unit, unitPrice: estimate?.unitPrice,
        ...(pack ? { purchaseQty: pack.qty, purchaseUnit: pack.unit, ...(ingredient.presentation && !PRESENTATION_IS_UNIT.test(ingredient.presentation.trim()) ? { presentation: ingredient.presentation } : {}) } : {}),
        ...(packPrice != null ? { packPrice } : {}), usedIn,
        text: priceQuestionText(language, ingredient.name, money, unit, estimate?.unitPrice, pack, ingredient.presentation, packPrice),
        options: estimate ? ['Sí', 'Otro precio'] : ['Agregar precio'] } });
  }
  for (const draft of catalog.drafts ?? []) {
    if (draft.kind !== 'recipe' || draft.status !== 'pending') continue;
    for (const [index, line] of planRecipeProposal(draft.payload, catalog.ingredients).lines.entries()) {
      if (line.match.kind !== 'ambiguous' || line.ingredientId) continue;
      ranked.push({ tier: 1, score: 0, question: { id: `choose-${draft.id}-${index}`, kind: 'choose', draftId: draft.id, line: index, text: `¿Qué ingrediente es «${line.ingredientName}»?`, options: line.match.candidates.map(({ ingredientId, name }) => ({ ingredientId, name })) } });
    }
  }
  const targets = catalog.menuItems.filter((item) => item.active);
  const dishes = targets.length ? targets.map((item) => ({ menuItemId: item.menuItemId, recipeId: item.recipeId, name: item.name })) : catalog.recipes.filter((item) => item.active && item.kind === 'plato').map((item) => ({ recipeId: item.recipeId, name: item.name }));
  for (const item of dishes) if (!context.recipes.get(item.recipeId)?.components.length) ranked.push({ tier: 2, score: sold.get(item.recipeId) ?? 0, question: { ...item, id: `recipe-${item.recipeId}`, kind: 'add-recipe', text: language === 'en' ? `Can you tell me how you make ${item.name}?` : language === 'pt' ? `Pode contar como prepara ${item.name}?` : `¿Me cuentas cómo va ${item.name}?` } });
  if (!dishes.length && completeness.nextActions.some((item) => item.kind === 'add-recipe')) ranked.push({ tier: 2, score: 0, question: { id: 'first-recipe', kind: 'add-recipe', text: language === 'en' ? 'Can you tell me about your signature dish?' : language === 'pt' ? 'Pode contar como prepara o seu prato especial?' : '¿Me cuentas tu plato estrella?' } });
  return ranked.filter(({ question }) => (catalog.history?.[question.id]?.archivedUntil ?? 0) <= now)
    .sort((a, b) => {
      const skippedA = catalog.history?.[a.question.id]?.lastSkippedAt ?? 0;
      const skippedB = catalog.history?.[b.question.id]?.lastSkippedAt ?? 0;
      return Number(Boolean(skippedA)) - Number(Boolean(skippedB)) || (skippedA && skippedB ? skippedA - skippedB : 0) || a.tier - b.tier || b.score - a.score || a.question.id.localeCompare(b.question.id);
    }).slice(0, Math.max(0, limit)).map(({ question }) => question);
}

export function skipQuestion(history: QuestionHistory | undefined, now = Date.now()): QuestionHistory {
  const previous = history?.archivedUntil && history.archivedUntil <= now ? 0 : history?.skips ?? 0;
  const skips = previous + 1;
  return { skips, lastSkippedAt: now, ...(skips >= 2 ? { archivedUntil: now + 30 * 86400000 } : {}) };
}

export function localQuestionTime(now: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return { date: `${value('year')}-${value('month')}-${value('day')}`, hour: Number(value('hour')) };
}

/** «kg», «1 kg», «litro», «unidad»: no es un envase, es la unidad repetida. */
const PRESENTATION_IS_UNIT = /^(\d+([.,]\d+)?\s*)?(kg|kgs|kilos?|g|gr|gramos?|l|lt|litros?|ml|cl|pz|pzas?|piezas?|uds?|unidad(es)?|docenas?|lb|libras?|oz|onzas?|gal|gals|fl\.?\s*oz)\.?$/i;

const UNIT_LABEL: Partial<Record<Unit, string>> = { l: 'L', ml: 'ml', cl: 'cl', pz: 'piezas', docena: 'docenas', floz: 'fl oz', gal: 'gal' };
export function unitLabel(unit: Unit, qty = 1): string {
  if (unit === 'pz') return qty === 1 ? 'pieza' : 'piezas';
  if (unit === 'docena') return qty === 1 ? 'docena' : 'docenas';
  return UNIT_LABEL[unit] ?? unit;
}

/** El formato de compra del ingrediente, si es de la misma familia que la unidad del precio. */
function purchaseFormat(ingredient: Ingredient, unit: Unit): { qty: number; unit: PurchaseUnit } | undefined {
  const { purchaseQty, purchaseUnit } = ingredient;
  if (!purchaseQty || purchaseQty <= 0 || !purchaseUnit || !isPurchaseUnit(purchaseUnit)) return undefined;
  if (dimensionOf(purchaseUnit) !== dimensionOf(unit)) return undefined;
  return { qty: purchaseQty, unit: purchaseUnit };
}

function priceQuestionText(language: string, name: string, money: Intl.NumberFormat, unit: Unit, unitPrice: number | undefined, pack: { qty: number; unit: PurchaseUnit } | undefined, presentation: string | undefined, packPrice: number | undefined): string {
  if (language === 'en') return unitPrice != null ? `${name} at ~${money.format(unitPrice)}/${unit}?` : `How much do you pay per ${unit} of ${name}?`;
  if (language === 'pt') return unitPrice != null ? `${name} a ~${money.format(unitPrice)}/${unit}?` : `Quanto paga por ${unit} de ${name}?`;
  // Siempre en el formato en que se compra: «¿Pagas unos $15 por una bolsa de 1 kg de sal fina?».
  const envase = presentation && !PRESENTATION_IS_UNIT.test(presentation.trim()) ? presentation.trim().toLowerCase() : '';
  const format = pack ? `${envase ? `${envase} de ` : ''}${formatCifra(pack.qty, money.resolvedOptions().locale)} ${unitLabel(pack.unit, pack.qty)}` : undefined;
  if (packPrice != null && format) return `¿Pagas unos ${money.format(packPrice)} por ${format} de ${name.trim().toLowerCase()}?`;
  if (format) return `¿Cuánto pagas por ${format} de ${name.trim().toLowerCase()}?`;
  return `¿En qué compras ${name.trim().toLowerCase()} y cuánto pagas?`;
}

/** Respuesta a una pregunta de precio: o se acepta la estimacion, o se dice el precio de lo que se compra. */
export interface PriceAnswer {
  /** Precio por `question.unit` (WhatsApp, version antigua de la app). */
  unitPrice?: number;
  /** Precio del formato de compra, sin impuestos: «pago 18 por la bolsa de 1 kg». */
  price?: number;
  qty?: number;
  unit?: PurchaseUnit;
  presentation?: string;
}

/**
 * Lo que se guarda en el ingrediente al responder. Nunca pisa el formato de
 * compra con «1 kg» (fallo visto el 11 oct 2026): si el ingrediente ya tiene
 * formato, el precio se pasa a ese formato.
 */
export function resolvePriceAnswer(ingredient: Pick<Ingredient, 'purchaseQty' | 'purchaseUnit' | 'presentation'>, question: Extract<Question, { kind: 'confirm-price' }>, answer: PriceAnswer): { price: number; qty: number; unit: PurchaseUnit; presentation?: string } {
  const valid = (value: number | undefined): value is number => value != null && Number.isFinite(value) && value > 0;
  if (valid(answer.price) && valid(answer.qty) && answer.unit && isPurchaseUnit(answer.unit)) {
    const presentation = answer.presentation?.trim();
    return { price: answer.price, qty: answer.qty, unit: answer.unit, ...(presentation ? { presentation } : {}) };
  }
  if (answer.unitPrice == null && valid(question.packPrice) && valid(question.purchaseQty) && question.purchaseUnit) {
    return { price: question.packPrice, qty: question.purchaseQty, unit: question.purchaseUnit };
  }
  const perUnit = answer.unitPrice ?? question.unitPrice;
  if (!valid(perUnit)) throw new Error('Escribe un precio mayor que cero.');
  const pack = purchaseFormat(ingredient as Ingredient, question.unit);
  if (pack) return { price: perUnit * toBase(pack.qty, pack.unit) / toBase(1, question.unit), qty: pack.qty, unit: pack.unit };
  if (!isPurchaseUnit(question.unit)) throw new Error('Esa unidad no sirve como formato de compra.');
  return { price: perUnit, qty: 1, unit: question.unit };
}
